import { config } from 'config';
import chroma from 'chroma-js';
import { number } from './utils/number.js';
import { legendProportional } from './gui/legendProportional.js';

export const RANGE_ZERO = 0,
  RANGE_POSITIVE = 1,
  RANGE_NEGATIVE = 2,
  RANGE_MIXED = 3,
  RANGE_NULL = 4;

/**
 * Data of the selected row.
 */
export const dataSeries = {

  /**
   * @type {JsonStatReader} NAFIDAS JSON-stat reader
   */
  _reader: null,

  /**
   * store the last accessed data series
   * @type {DataSeries}
   */
  _cache: null,

  /**
   * @param {JsonStatReader} jsonStatReader
   */
  init (jsonStatReader) {
    this._reader = jsonStatReader;
  },

  /**
   * Return the data series at given offset.
   * If no offset is given, the previously accessed data series is returned.
   * @param {Number} [rowIdx]
   * @return {DataSeries}
   */
  get (rowIdx) {
    if (rowIdx === undefined) {
      return this._cache;
    }

    const data = this._reader.getDataSeries(rowIdx);
    this._addMinMax(data);
    this._addRangeType(data);
    this._cache = data;

    return data;
  },

  /**
   * Create a text consisting of label, value and error.
   * @param {DataSeriesItem} item
   * @param {Boolean} withLabel
   * @return {String}
   */
  createStr (item, withLabel = true) {
    const numDec = this._reader.getDecimals();
    let value = item.value === null ? config.nullChar : item.value.toFixed(numDec);
    const label = withLabel ? item.label + ':' : '';
    const error = item.error === null ? '.' : item.error;
    const unit = this._reader.getErrorSymbol();

    return label + ' ' + value + ' ± ' + error + unit;
  },

  /**
   *
   * @param {DataSeries} data
   * @param scale
   */
  addColor (data, scale) {
    let color;
    const alpha = config.color.region.opacity;
    data.series.forEach((item, i) => {
      if (item.value === null) {
        color = chroma(config.color.region.none);
      } else {
        color = scale(item.value);
      }
      item.color = color.alpha(alpha).hex();
    });
  },

  /**
   * Add a radius property to each item.
   * @param {DataSeries} data
   */
  addRadius (data) {
    data.series.forEach((item, i) => {
      const absMax = number.absMax(data.min, data.max);
      item.radius = legendProportional.calcRadius(item.value, absMax);
    });
  },

  /**
   * Add the minimum and the maximum of the values.
   * @param {DataSeries} data
   * @private
   */
  _addMinMax (data) {
    const vals = data.series.map(item => item.value);
    data.min = Math.min(...vals);
    data.max = Math.max(...vals);

    return data;
  },

  /**
   * Set the type of the value range.
   * @param {DataSeries} data
   * @private
   */
  _addRangeType (data) {
    switch (true) {
      case (data.max > 0 && data.min >= 0):
        data.range = RANGE_POSITIVE;
        break;
      case (data.max <= 0 && data.min < 0):
        data.range = RANGE_NEGATIVE;
        break;
      case (data.max > 0 && data.min < 0):
        data.range = RANGE_MIXED;
        break;
      case (data.max === 0 && data.min === 0):
        data.range = RANGE_ZERO;
        break;
      default:
        data.range = RANGE_NULL;
    }
  },

  /**
   * Return the data series with unique values only.
   * @param {Array} arr
   * @return {any[]}
   */
  unique (arr) {
    return arr.reduce((filtered, item) => {
      if (filtered.indexOf(item.value) === -1) {
        filtered.push(item.value);
      }
      return filtered;
    }, []);
  },

  /**
   * Add the gradient colors for positive values.
   * @private
   * @param {DataSeries} data
   */
  _positive (data) {
    return data.series.filter(item => item.value >= 0);
  },

  /**
   * Add the gradient colors for negative values.
   * @param {DataSeries} data
   * @param {Boolean} inclZero include the zero in the gradient
   * @private
   */
  _negative (data, inclZero) {
    return data.series.filter(item => {
      let expr;
      if (inclZero) {
        expr = item.value <= 0;
      } else {
        expr = item.value < 0;
      }
      return expr;
    });
  },
};